import { useEffect, useMemo, useRef, useState } from "react";
import {
	Body1,
	Button,
	Caption1,
	Checkbox,
	Divider,
	Link,
	Spinner,
	Text,
	makeStyles,
	tokens,
} from "@fluentui/react-components";
import {
	ChevronDown20Regular,
	ChevronUp20Regular,
	Open16Regular,
} from "@fluentui/react-icons";
import { useIntl } from "react-intl";
import {
	CrmProvider,
	CrmCallerContext,
	CrmMatch,
	CrmMatchedCase,
} from "@/common/models/callSession";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";

const ACCOUNT_LABEL_ID = "Salesforce.Contact.AccountLabel";

const setsEqual = (a: Set<string>, b: Set<string>): boolean => {
	if (a.size !== b.size) {
		return false;
	}
	for (const v of a) {
		if (!b.has(v)) {
			return false;
		}
	}
	return true;
};

/**
 * Derives a human-friendly label from a workflow variable name like "sf_contact_email".
 * Admin-defined record fields land as `<prefix>_<lookupObject>_<field>` (see the admin editor's
 * deriveCc4AllField): the editor stamps `sf_` for Salesforce, `dyn_` for Dynamics, and `hub_` for
 * HubSpot (variablePrefixFor), so the strip rule accepts any of those prefixes. Stripping the
 * `<prefix>_<object>_` gets us "email"; humanising underscores and capitalising gives "Email".
 */
const formatAttributeLabel = (key: string): string => {
	const stripped = key.replace(/^(?:sf|dyn|hub)_[a-z0-9]+_/i, "");
	const humanised = stripped.replace(/_/g, " ").trim();
	if (humanised.length === 0) {
		return key;
	}
	return humanised.charAt(0).toUpperCase() + humanised.slice(1);
};

const useStyles = makeStyles({
	root: {
		// flexShrink:0 keeps the picker from being squashed to 0px when sibling content
		// (e.g. the transcription panel with height:100%) takes flex-grow priority. Without
		// this the picker visually disappears as soon as transcription is started.
		flexShrink: 0,
		// Cap the picker's height so a matched caller with several cases doesn't crowd out the
		// conversation/transcription below it; the match list scrolls internally past this point.
		// Kept tight on webchat, where the panel sits above the chat and every row counts.
		maxHeight: "30vh",
		display: "flex",
		flexDirection: "column",
		margin: "8px 16px 0 16px",
		padding: "8px 16px",
		borderRadius: tokens.borderRadiusMedium,
		border: `1px solid ${tokens.colorNeutralStroke2}`,
		backgroundColor: tokens.colorNeutralBackground1,
		boxShadow: tokens.shadow2,
	},
	header: {
		display: "flex",
		alignItems: "center",
		columnGap: "8px",
		marginBottom: "6px",
		flexShrink: 0,
		cursor: "pointer",
		userSelect: "none",
	},
	headerCollapsed: {
		marginBottom: 0,
	},
	spinner: {
		marginLeft: "auto",
	},
	chevron: {
		marginLeft: "auto",
		minWidth: "24px",
	},
	providerChip: {
		flexShrink: 0,
		alignSelf: "center",
		padding: "1px 8px",
		borderRadius: tokens.borderRadiusCircular,
		backgroundColor: tokens.colorNeutralBackground4,
		color: tokens.colorNeutralForeground2,
		fontSize: tokens.fontSizeBase200,
		lineHeight: tokens.lineHeightBase200,
		whiteSpace: "nowrap",
	},
	divider: {
		marginBottom: "4px",
		flexShrink: 0,
	},
	matchList: {
		display: "flex",
		flexDirection: "column",
		rowGap: "6px",
		// Vertical scroll lives at the list level so a single match expanded with many cases
		// can extend past the panel cap without breaking the panel header's sticky behaviour.
		overflowY: "auto",
		minHeight: 0,
	},
	matchCard: {
		display: "flex",
		flexDirection: "column",
		borderRadius: tokens.borderRadiusSmall,
		border: `1px solid ${tokens.colorNeutralStroke2}`,
		backgroundColor: tokens.colorNeutralBackground2,
	},
	matchHeader: {
		display: "flex",
		alignItems: "center",
		columnGap: "8px",
		padding: "6px 10px",
		cursor: "pointer",
		userSelect: "none",
	},
	matchHeaderText: {
		display: "flex",
		flexDirection: "column",
		minWidth: 0,
		flexGrow: 1,
	},
	matchTitle: {
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	matchSubtitle: {
		color: tokens.colorNeutralForeground3,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	matchBody: {
		display: "grid",
		// The contact column sizes to its content (capped) so the related records sit right
		// beside it. A 50/50 split pushed the records to mid-panel, leaving a large empty gap
		// to their left whenever the contact had only a field or two. minmax(0, ...) on the
		// records column lets a long subject ellipsise instead of widening the row.
		gridTemplateColumns: "fit-content(340px) minmax(0, 1fr)",
		columnGap: "24px",
		padding: "0 10px 8px 10px",
	},
	column: {
		display: "flex",
		flexDirection: "column",
		minWidth: 0,
	},
	columnTitle: {
		display: "block",
		marginBottom: "4px",
		color: tokens.colorNeutralForeground3,
		flexShrink: 0,
	},
	contactSecondary: {
		display: "block",
		color: tokens.colorNeutralForeground3,
		marginBottom: "6px",
	},
	attrList: {
		display: "flex",
		flexDirection: "column",
		rowGap: "4px",
	},
	attrRow: {
		// Label and value share one line (label left, value right) to keep each mapped
		// field to a single row — stacking them doubled the panel's height.
		display: "flex",
		flexDirection: "row",
		alignItems: "baseline",
		columnGap: "6px",
	},
	attrLabel: {
		color: tokens.colorNeutralForeground3,
		flexShrink: 0,
		whiteSpace: "nowrap",
	},
	attrValue: {
		minWidth: 0,
		wordBreak: "break-word",
	},
	caseList: {
		display: "flex",
		flexDirection: "column",
		// Rows hug their content rather than stretching to the full records column, so a
		// row's hover highlight wraps just the checkbox + record instead of a wide empty band.
		alignItems: "flex-start",
		rowGap: "4px",
	},
	row: {
		display: "flex",
		// Rows are a single line now, so centre the checkbox against the text instead of
		// top-aligning it.
		alignItems: "center",
		padding: "2px 6px",
		borderRadius: tokens.borderRadiusSmall,
		":hover": {
			backgroundColor: tokens.colorNeutralBackground1Hover,
		},
	},
	// Same metrics as `row` but non-interactive: used for a monitoring supervisor, who sees
	// the related records (no checkbox) and so gets no hover/selection affordance.
	readOnlyRow: {
		display: "flex",
		alignItems: "center",
		padding: "2px 6px",
	},
	checkbox: {
		// Fluent wraps the 16px indicator in ~8px of margin on every side for a larger hit
		// area. That made each row taller than its text AND left a wide dead strip to the
		// left of the box, so we zero the margin and control spacing ourselves: the row's
		// padding sets the inset on the left, the checkbox's marginRight the gap to the text
		// (kept on the checkbox, not rowText, so a read-only row with no checkbox has no
		// dangling indent).
		paddingTop: 0,
		paddingBottom: 0,
		marginRight: "8px",
		"& .fui-Checkbox__indicator": {
			marginTop: 0,
			marginBottom: 0,
			marginLeft: 0,
			marginRight: 0,
		},
	},
	rowText: {
		// Record number/title and its priority·status share one line, left-grouped: the
		// title ellipsises if the row runs out of room, the status never wraps.
		display: "flex",
		flexDirection: "row",
		alignItems: "baseline",
		columnGap: "8px",
		minWidth: 0,
		flexGrow: 1,
	},
	caseTitle: {
		// Sized to its content so the priority·status that follows sits right next to the
		// record number (columnGap) instead of being pushed to the far right of the column.
		// minWidth:0 still lets a long subject ellipsise when the row runs out of room.
		minWidth: 0,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	openIcon: {
		verticalAlign: "middle",
		marginLeft: "4px",
	},
	priorityStatus: {
		color: tokens.colorNeutralForeground3,
		flexShrink: 0,
		whiteSpace: "nowrap",
	},
});

interface CrmCasePickerProps {
	/**
	 * The Salesforce context to render. Voice flows pass `callSession?.crmCallerContext`
	 * from the call-session store; messaging flows pass the value returned by the per-session
	 * GET /v2/me/messaging/{sessionId}/crm-context endpoint. Null/undefined hides the
	 * picker entirely (e.g. SF disabled or lookup didn't match).
	 */
	sfCtx: CrmCallerContext | null | undefined;
	/**
	 * Persists the agent's selection on the backend. Voice wires this to
	 * `setCrmSelectedCases` (POST /v2/me/call/salesforce/cases); messaging wires it
	 * to the per-session messaging endpoint. The picker awaits this on each toggle and
	 * rolls back the optimistic update on rejection.
	 */
	onSaveSelectedCases: (selectedCaseIds: string[]) => Promise<void>;
	/**
	 * Read-only mode for a supervisor who is monitoring the interaction (voice: the call
	 * session is in the Monitoring state; messaging: `useTextMonitoring()`). The related
	 * records are still shown so the supervisor has the caller's context, but the selection
	 * checkboxes are omitted entirely — case selection belongs to the handling agent.
	 */
	readOnly?: boolean;
	/**
	 * When true the picker renders nothing while staying mounted. Both mount sites pass their
	 * wrap-up/terminated state here instead of conditionally unmounting the picker
	 * (`{cond && <CrmCasePicker/>}`): the parent-driven unmount at wrap-up start was observed
	 * to leave the picker's DOM stranded in the workspace — still painted after the interaction
	 * closed, with its fiber detached (CC4ALL-9149 follow-up). Rendering null from inside the
	 * component removes the same DOM through the picker's own fiber instead of a parent
	 * child-list deletion during the busy wrap-up commit.
	 */
	hidden?: boolean;
}

/**
 * Reconstructs a synthetic {@link CrmMatch} from the top-level scalar fields of a
 * legacy context (persisted before the per-step Matches list existed). Lets old call sessions
 * still render in the new picker without a server-side migration.
 */
const toLegacyMatch = (sfCtx: CrmCallerContext): CrmMatch => ({
	lookupConfigurationName: sfCtx.lookupConfigurationName,
	contactId: sfCtx.contactId,
	contactName: sfCtx.contactName,
	contactViewUrl: sfCtx.contactViewUrl,
	accountId: sfCtx.accountId,
	accountName: sfCtx.accountName,
	accountViewUrl: sfCtx.accountViewUrl,
	openCaseCount: sfCtx.openCaseCount,
	cases: sfCtx.cases,
	caseTargetObject: sfCtx.caseTargetObject,
	customAttributes: sfCtx.customAttributes,
	matchType: sfCtx.matchType,
	lookupTimestamp: sfCtx.lookupTimestamp,
});

// Content-based key for a match (no array index — an index prefix would change a card's key on
// any reorder/prepend, remounting it and losing the agent's collapse state). Duplicates with the
// same content are disambiguated by the caller with an occurrence counter.
const matchContentKey = (m: CrmMatch): string =>
	`${m.lookupConfigurationId || m.lookupConfigurationName || "match"}-${m.contactId || ""}`;

/**
 * Renders the Salesforce caller context for an interaction as a panel with a collapsible card
 * per matched record. Each card surfaces the lookup's contact details (left column) and its
 * related cases/work orders/etc. with checkboxes (right column); selected case IDs are flat
 * across all matches.
 *
 * SignalR may push an updated context mid-interaction (e.g. a later IVR lookup step appends
 * a second match). The picker reconciles incoming `selectedCaseIds` only when no save is in
 * flight and the value either matches our last write or we have no pending edit — otherwise
 * a stale push could clobber the agent's in-progress selection.
 */
export const CrmCasePicker = ({
	sfCtx,
	onSaveSelectedCases,
	readOnly = false,
	hidden = false,
}: CrmCasePickerProps) => {
	const intl = useIntl();
	const classes = useStyles();

	// Effective match list: prefer the explicit list (new code path), fall back to
	// a synthesised one for old call sessions whose top-level fields are populated but
	// whose Matches array is empty.
	const matches: CrmMatch[] = useMemo(() => {
		if (sfCtx?.matches && sfCtx.matches.length > 0) {
			return sfCtx.matches;
		}
		if (sfCtx && (sfCtx.contactId || (sfCtx.cases && sfCtx.cases.length > 0))) {
			return [toLegacyMatch(sfCtx)];
		}
		return [];
	}, [sfCtx]);

	// Stable per-match keys aligned with `matches`: content-based, with an occurrence counter
	// (#1, #2, …) appended to duplicates so two matches sharing the same config+contact still get
	// distinct keys without falling back to the array index.
	const matchKeys: string[] = useMemo(() => {
		const counts = new Map<string, number>();
		return matches.map((m) => {
			const base = matchContentKey(m);
			const seen = counts.get(base) ?? 0;
			counts.set(base, seen + 1);
			return seen === 0 ? base : `${base}#${seen}`;
		});
	}, [matches]);

	const [selected, setSelected] = useState<Set<string>>(
		new Set(sfCtx?.selectedCaseIds ?? [])
	);
	const [saving, setSaving] = useState(false);
	// Default the whole panel open so the agent sees the matches when the call connects.
	const [expanded, setExpanded] = useState(true);
	// Per-match expansion. A single match defaults expanded (same UX as before); multi-match
	// defaults all collapsed so the agent can scan the contact names without scroll.
	const [expandedMatches, setExpandedMatches] = useState<Set<string>>(() => {
		if (matches.length === 1 && matchKeys[0]) {
			return new Set([matchKeys[0]]);
		}
		return new Set();
	});
	// When new matches arrive mid-call (a later lookup step appended), auto-expand the new
	// entry so the agent immediately notices it. We track keys we've already accounted for
	// so existing user-driven collapse choices aren't reverted.
	const seenKeysRef = useRef<Set<string>>(new Set(matchKeys));
	useEffect(() => {
		const additions = matchKeys.filter((k) => !seenKeysRef.current.has(k));
		if (additions.length === 0) {
			return;
		}
		setExpandedMatches((prev) => {
			const next = new Set(prev);
			for (const k of additions) {
				next.add(k);
			}
			return next;
		});
		seenKeysRef.current = new Set(matchKeys);
	}, [matchKeys]);

	const lastSentRef = useRef<Set<string>>(
		new Set(sfCtx?.selectedCaseIds ?? [])
	);
	const dirtyRef = useRef(false);

	useEffect(() => {
		if (saving) {
			return;
		}
		const incoming = new Set(sfCtx?.selectedCaseIds ?? []);
		const incomingMatchesLastSent = setsEqual(incoming, lastSentRef.current);
		if (dirtyRef.current && !incomingMatchesLastSent) {
			return;
		}
		dirtyRef.current = false;
		setSelected(incoming);
	}, [sfCtx?.selectedCaseIds, saving]);

	// Render nothing when the mount site hides the picker (wrap-up / terminated — see the
	// hidden prop doc), when there's no Salesforce context, or when there's no synthesised
	// match (SF disabled or lookup didn't match). A match with zero cases is still rendered
	// so the agent sees the contact match itself. Kept below every hook (rules-of-hooks).
	if (hidden || !sfCtx || matches.length === 0) {
		return null;
	}

	// Provider labelling: a single call can carry matches from more than one CRM (a tenant may run
	// Salesforce and Dynamics at once). A missing provider (legacy context) is treated as Salesforce.
	// When the panel mixes providers we title it generically ("CRM") and tag each card; a single-
	// provider panel just names the provider in the title and shows no chips (Salesforce-only is
	// therefore visually unchanged).
	const matchProviders = matches.map((m) => m.provider ?? CrmProvider.Salesforce);
	const hasMixedProviders = new Set(matchProviders).size > 1;
	let panelTitleId: string;
	if (hasMixedProviders) {
		panelTitleId = "Salesforce.Provider.Mixed";
	} else if (matchProviders[0] === CrmProvider.Dynamics) {
		panelTitleId = "Salesforce.Provider.Dynamics";
	} else if (matchProviders[0] === CrmProvider.HubSpot) {
		panelTitleId = "Salesforce.Provider.HubSpot";
	} else {
		panelTitleId = "Salesforce.Cases.Title";
	}

	const handleToggle = async (caseId: string, checked: boolean) => {
		// Defensive: a monitoring supervisor never renders the checkboxes, but never let a
		// stray toggle reach the save endpoint either.
		if (readOnly) {
			return;
		}
		const next = new Set(selected);
		if (checked) {
			next.add(caseId);
		} else {
			next.delete(caseId);
		}
		const previous = new Set(selected);
		setSelected(next);
		dirtyRef.current = true;

		setSaving(true);
		addBreadcrumb("ui_interaction", "Salesforce case selection changed", {
			caseId,
			checked,
			total: next.size,
		});
		try {
			await onSaveSelectedCases(Array.from(next));
			lastSentRef.current = next;
		} catch (ex: any) {
			setSelected(previous);
			dirtyRef.current = false;
			captureError(
				ex,
				"Failed to save Salesforce case selection",
				undefined,
				{ selectedCount: next.size }
			);
		} finally {
			setSaving(false);
		}
	};

	const togglePanel = () => setExpanded((v) => !v);
	const toggleMatch = (key: string) =>
		setExpandedMatches((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});

	const panelToggleLabelId = expanded
		? "Salesforce.Picker.Collapse"
		: "Salesforce.Picker.Expand";

	return (
		<div className={classes.root}>
			<div
				className={`${classes.header} ${expanded ? "" : classes.headerCollapsed}`}
				onClick={togglePanel}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						togglePanel();
					}
				}}
				aria-expanded={expanded}
			>
				<Text size={400} weight="semibold">
					{intl.formatMessage({ id: panelTitleId })}
				</Text>
				{saving && <Spinner size="tiny" className={classes.spinner} />}
				<Button
					appearance="transparent"
					size="small"
					className={saving ? undefined : classes.chevron}
					icon={
						expanded ? <ChevronUp20Regular /> : <ChevronDown20Regular />
					}
					aria-label={intl.formatMessage({ id: panelToggleLabelId })}
					onClick={(e) => {
						e.stopPropagation();
						togglePanel();
					}}
				/>
			</div>
			{expanded && <Divider className={classes.divider} />}
			{expanded && (
				<div className={classes.matchList}>
					{matches.map((match, index) => {
						// matchKeys is aligned 1:1 with matches; the fallback only satisfies the
						// indexed-access type (string | undefined) and never actually runs.
						const key = matchKeys[index] ?? matchContentKey(match);
						const isOpen = expandedMatches.has(key);
						const cases: CrmMatchedCase[] = match.cases ?? [];
						const matchProvider = match.provider ?? CrmProvider.Salesforce;
						const isCaseTarget =
							!match.caseTargetObject ||
							match.caseTargetObject.toLowerCase() === "case";
						const contactDisplay =
							match.contactName ||
							match.contactId ||
							intl.formatMessage({ id: "Salesforce.Contact.Unknown" });
						// Person-Account orgs store the account name equal to the contact name;
						// only surface the account when it actually differs from the header title.
						const contactNorm = contactDisplay.trim().toLowerCase();
						const accountNorm =
							match.accountName?.trim().toLowerCase() ?? "";
						const showAccount =
							accountNorm !== "" && accountNorm !== contactNorm;
						// Drop mapped fields that merely restate the contact/account name (e.g. a
						// "Name" field equal to the header) or are blank — they repeat what the
						// header already shows and only add height.
						const customAttributes = Object.entries(
							match.customAttributes ?? {}
						).filter(([, value]) => {
							const v = value?.trim().toLowerCase() ?? "";
							return v !== "" && v !== contactNorm && v !== accountNorm;
						});
						const subtitleParts: string[] = [];
						if (showAccount) {
							subtitleParts.push(
								intl.formatMessage(
									{ id: ACCOUNT_LABEL_ID },
									{ account: match.accountName }
								)
							);
						}
						subtitleParts.push(
							intl.formatMessage(
								{ id: "Salesforce.Cases.Subtitle" },
								{ count: cases.length }
							)
						);
						return (
							<div key={key} className={classes.matchCard}>
								<div
									className={classes.matchHeader}
									onClick={() => toggleMatch(key)}
									role="button"
									tabIndex={0}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											toggleMatch(key);
										}
									}}
									aria-expanded={isOpen}
								>
									<div className={classes.matchHeaderText}>
										<Body1 className={classes.matchTitle}>
											{match.contactViewUrl ? (
												<Link
													href={match.contactViewUrl}
													target="_blank"
													rel="noopener noreferrer"
													onClick={(e) => e.stopPropagation()}
													onKeyDown={(e) => e.stopPropagation()}
												>
													{contactDisplay}
													<Open16Regular
														className={classes.openIcon}
													/>
												</Link>
											) : (
												contactDisplay
											)}
										</Body1>
										{!isOpen && (
											<Caption1 className={classes.matchSubtitle}>
												{subtitleParts.join(" · ")}
											</Caption1>
										)}
									</div>
									{hasMixedProviders && (
										<span className={classes.providerChip}>
											{intl.formatMessage({
												id: `Salesforce.Provider.${matchProvider}`,
											})}
										</span>
									)}
									<Button
										appearance="transparent"
										size="small"
										icon={
											isOpen ? (
												<ChevronUp20Regular />
											) : (
												<ChevronDown20Regular />
											)
										}
										aria-label={intl.formatMessage({
											id: isOpen
												? "Salesforce.Match.Collapse"
												: "Salesforce.Match.Expand",
										})}
										onClick={(e) => {
											e.stopPropagation();
											toggleMatch(key);
										}}
									/>
								</div>
								{isOpen && (
									<div className={classes.matchBody}>
										<div className={classes.column}>
											<Caption1 className={classes.columnTitle}>
												{intl.formatMessage({
													id: "Salesforce.Contact.Title",
												})}
											</Caption1>
											{showAccount && (
												<Caption1 className={classes.contactSecondary}>
													{match.accountViewUrl ? (
														<Link
															href={match.accountViewUrl}
															target="_blank"
															rel="noopener noreferrer"
														>
															{intl.formatMessage(
																{ id: ACCOUNT_LABEL_ID },
																{ account: match.accountName }
															)}
															<Open16Regular
																className={classes.openIcon}
															/>
														</Link>
													) : (
														intl.formatMessage(
															{ id: ACCOUNT_LABEL_ID },
															{ account: match.accountName }
														)
													)}
												</Caption1>
											)}
											{customAttributes.length > 0 ? (
												<div className={classes.attrList}>
													{customAttributes.map(([k, value]) => (
														<div key={k} className={classes.attrRow}>
															<Caption1 className={classes.attrLabel}>
																{`${formatAttributeLabel(k)}:`}
															</Caption1>
															<Body1 className={classes.attrValue}>
																{value}
															</Body1>
														</div>
													))}
												</div>
											) : (
												// No distinct account and no extra fields: the header link
												// is the only identity, so restate it so the column isn't
												// blank beside the related records.
												!showAccount && (
													<Body1 className={classes.attrValue}>
														{contactDisplay}
													</Body1>
												)
											)}
										</div>
										<div className={classes.column}>
											<Caption1 className={classes.columnTitle}>
												{intl.formatMessage(
													{ id: "Salesforce.Cases.Subtitle" },
													{ count: cases.length }
												)}
											</Caption1>
											<div className={classes.caseList}>
												{cases.length === 0 && (
													<Caption1 className={classes.matchSubtitle}>
														{intl.formatMessage({
															id: "Salesforce.Match.NoCases",
														})}
													</Caption1>
												)}
												{cases.map((c) => {
													const titleParts: string[] = [];
													if (!isCaseTarget && match.caseTargetObject) {
														titleParts.push(match.caseTargetObject);
													}
													if (c.caseNumber) {
														titleParts.push(c.caseNumber);
													}
													if (c.subject) {
														titleParts.push(c.subject);
													}
													const title =
														titleParts.length > 0
															? titleParts.join(" — ")
															: intl.formatMessage({
																	id: "Salesforce.Cases.MissingCaseNumber",
																});
													const hasPriorityOrStatus =
														!!c.priority || !!c.status;
													// A monitoring supervisor sees the related records but can't
													// change the agent's selection: render a plain, non-interactive
													// row (no checkbox, no label/hover). The handling agent gets a
													// <label> so a click anywhere on the row toggles the checkbox.
													const RowTag = readOnly ? "div" : "label";
													return (
														<RowTag
															key={c.id}
															className={
																readOnly
																	? classes.readOnlyRow
																	: classes.row
															}
														>
															{!readOnly && (
																<Checkbox
																	className={classes.checkbox}
																	checked={selected.has(c.id)}
																	onChange={(_, data) =>
																		handleToggle(c.id, data.checked === true)
																	}
																	disabled={saving}
																/>
															)}
															<div className={classes.rowText}>
																<Body1 className={classes.caseTitle}>
																	{c.viewUrl ? (
																		<Link
																			href={c.viewUrl}
																			target="_blank"
																			rel="noopener noreferrer"
																			onClick={(e) => e.stopPropagation()}
																		>
																			{title}
																			<Open16Regular
																				className={classes.openIcon}
																			/>
																		</Link>
																	) : (
																		title
																	)}
																</Body1>
																{hasPriorityOrStatus && (
																	<Caption1
																		className={classes.priorityStatus}
																	>
																		{intl.formatMessage(
																			{ id: "Salesforce.Cases.PriorityStatus" },
																			{
																				priority: c.priority ?? "—",
																				status: c.status ?? "—",
																			}
																		)}
																	</Caption1>
																)}
															</div>
														</RowTag>
													);
												})}
											</div>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
